import { Switch, Route, Router as WouterRouter, useLocation } from "wouter";
import { lazy, Suspense, useEffect } from "react";
import type { ComponentType } from "react";
import { LazyMotion, MotionConfig, domAnimation } from "framer-motion";
import { Toaster } from "@/components/ui/toaster";
import { CookieConsent } from "@/components/layout/CookieConsent";
import { FloatingContact } from "@/components/layout/FloatingContact";
import { RouteProgress } from "@/components/layout/RouteProgress";
import NotFound from "@/pages/not-found";
import { BLOG_POSTS } from "@/data/blog";
import { SERVICE_PAGE_SLUGS } from "@/data/servicePageSlugs";
import { scrollToId } from "@/lib/scroll";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

type PageModule = { default: ComponentType };

// Each route is its own chunk, so a visitor only downloads the code for the
// page they're on (Lighthouse "Reduce unused JavaScript"). Both main.tsx and
// entry-server.tsx preload the route's chunk before rendering, so the
// prerendered HTML and the client's first render are identical and React
// hydrates the existing markup instead of replacing it.
//
// `source` is the page's path relative to the project root. It must match
// the module that `load` imports: scripts/prerender.mjs looks it up in the
// Vite manifest to add <link rel="modulepreload"> tags for the page's chunks,
// and fails the build if it isn't found.
function lazyPage(source: string, load: () => Promise<PageModule>) {
  let Loaded: ComponentType | null = null;
  const LazyPage = lazy(load);
  const preload = () =>
    load().then((mod) => {
      Loaded = mod.default;
    });

  // Once preloaded, render synchronously instead of through React.lazy, so
  // the first render never suspends and never flashes the Suspense fallback
  // over the prerendered markup.
  function Page() {
    return Loaded ? <Loaded /> : <LazyPage />;
  }

  return Object.assign(Page, { preload, source });
}

// Every blog post shares one page chunk; the page picks the post by slug.
const BlogPostPage = lazyPage("src/pages/BlogPost.tsx", () => import("@/pages/BlogPost"));
// Likewise every service landing page; the page picks its content by slug.
const ServiceDetailPage = lazyPage("src/pages/ServiceDetail.tsx", () => import("@/pages/ServiceDetail"));

const PAGES: Record<string, ReturnType<typeof lazyPage>> = {
  "/": lazyPage("src/pages/Home.tsx", () => import("@/pages/Home")),
  "/services": lazyPage("src/pages/Services.tsx", () => import("@/pages/Services")),
  ...Object.fromEntries(SERVICE_PAGE_SLUGS.map((slug) => [`/services/${slug}`, ServiceDetailPage])),
  "/pricing": lazyPage("src/pages/Pricing.tsx", () => import("@/pages/Pricing")),
  "/training": lazyPage("src/pages/Training.tsx", () => import("@/pages/Training")),
  "/careers": lazyPage("src/pages/Careers.tsx", () => import("@/pages/Careers")),
  "/blog": lazyPage("src/pages/Blog.tsx", () => import("@/pages/Blog")),
  ...Object.fromEntries(BLOG_POSTS.map((post) => [`/blog/${post.slug}`, BlogPostPage])),
  "/contact": lazyPage("src/pages/Contact.tsx", () => import("@/pages/Contact")),
  "/privacy-policy": lazyPage("src/pages/PrivacyPolicy.tsx", () => import("@/pages/PrivacyPolicy")),
};

export const ROUTES = Object.keys(PAGES);

/** Route -> page source path, for the prerender's modulepreload tags. */
export const ROUTE_SOURCES: Record<string, string> = Object.fromEntries(
  Object.entries(PAGES).map(([path, page]) => [path, page.source]),
);

function pageFor(pathname: string) {
  const path = pathname.startsWith(BASE) ? pathname.slice(BASE.length) || "/" : pathname;
  return PAGES[path.replace(/(.)\/$/, "$1")];
}

/** Loads the chunk for `pathname` (if it's a known route). Resolves either way. */
export function preloadRoute(pathname: string): Promise<void> {
  return pageFor(pathname)?.preload().catch(() => {}) ?? Promise.resolve();
}

// Start fetching a page's chunk as soon as the visitor shows intent to
// navigate to it (hover, focus, or touch on a link), so client-side
// navigation is effectively instant without loading every page up front.
function usePrefetchOnIntent() {
  useEffect(() => {
    const onIntent = (e: Event) => {
      const link = (e.target as Element | null)?.closest?.("a[href]");
      if (!link) return;
      const url = new URL((link as HTMLAnchorElement).href, window.location.href);
      if (url.origin === window.location.origin) preloadRoute(url.pathname);
    };
    const events = ["pointerover", "focusin", "touchstart"] as const;
    events.forEach((type) => document.addEventListener(type, onIntent, { passive: true }));
    return () => events.forEach((type) => document.removeEventListener(type, onIntent));
  }, []);
}

// New pages open at the top, unless the URL has a #section (e.g. the
// navbar's Services submenu links to /services#web-development), in which
// case we scroll to that section once it has rendered.
function ScrollToTop() {
  const [location] = useLocation();
  useEffect(() => {
    const id = decodeURIComponent(window.location.hash.slice(1));
    if (!id) {
      window.scrollTo(0, 0);
      return;
    }
    return scrollToId(id, "auto", () => window.scrollTo(0, 0));
  }, [location]);
  return null;
}

// Feeds the mouse position to the hovered card's glow (`.card-fx` in index.css).
function useCardGlow() {
  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      if (e.pointerType !== "mouse") return;
      const card = (e.target as Element | null)?.closest?.<HTMLElement>(".card-fx");
      if (!card) return;
      const rect = card.getBoundingClientRect();
      card.style.setProperty("--mx", `${e.clientX - rect.left}px`);
      card.style.setProperty("--my", `${e.clientY - rect.top}px`);
    };
    document.addEventListener("pointermove", onMove, { passive: true });
    return () => document.removeEventListener("pointermove", onMove);
  }, []);
}

function Router() {
  usePrefetchOnIntent();
  useCardGlow();

  return (
    <>
      <ScrollToTop />
      <RouteProgress />
      <Suspense fallback={<div className="min-h-screen" />}>
        <Switch>
          {Object.entries(PAGES).map(([path, Page]) => (
            <Route key={path} path={path} component={Page} />
          ))}
          <Route component={NotFound} />
        </Switch>
      </Suspense>
    </>
  );
}

function App({ ssrPath }: { ssrPath?: string }) {
  return (
    // Components use the lightweight `m` instead of `motion`, with only the
    // DOM animation features the site needs (initial/animate/whileInView),
    // which keeps most of framer-motion out of the bundle. `strict` throws
    // if a full `motion` component sneaks back in.
    <LazyMotion features={domAnimation} strict>
      {/* Visitors who ask their device for reduced motion get the fades
          without the movement. */}
      <MotionConfig reducedMotion="user">
        <WouterRouter base={BASE} ssrPath={ssrPath}>
          <Router />
        </WouterRouter>
        <FloatingContact />
        <CookieConsent />
        <Toaster />
      </MotionConfig>
    </LazyMotion>
  );
}

export default App;
